import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Spin,
  Typography,
  message as antdMessage,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  GlobalOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import {
  useConfirmScrapeMutation,
  useScrapeEstimate,
  useStartScrapeEstimateMutation,
  type ScrapeContacts,
  type ScrapeEstimateErrorReason,
  type ScrapeEstimateReady,
} from '@entities/source';
import { extractErrorMessage } from '@shared/api';

const { Title, Text, Link } = Typography;

interface Props {
  assistantId: string;
  onBack: () => void;
  /** После успешного confirm — переключиться на карточку нового источника. */
  onSourceConfirmed: (sourceId: string) => void;
}

interface FormValues {
  url: string;
  process_images: boolean;
}

/**
 * Локальный sub-state внутри AddSiteState.
 *
 *  - 'form' — юзер ещё не нажал «Сканировать»
 *  - 'scanning' — scout в работе, поллинг
 *  - 'ready' — превью получено, показываем цену + кнопку «Подтвердить»
 *  - 'failed' — scout вернул ошибку, показываем что делать
 *
 * Хранится в useState (не в URL). При onBack — сброс в 'form'.
 */
type SubState =
  | { kind: 'form' }
  | { kind: 'scanning'; sourceId: string }
  | { kind: 'ready'; sourceId: string }
  | { kind: 'failed'; sourceId: string };

/** Максимальное время ожидания scout. Если дольше — показываем timeout. */
const SCOUT_TIMEOUT_MS = 5 * 60 * 1000;

export function AddSiteState({ assistantId, onBack, onSourceConfirmed }: Props) {
  const [substate, setSubstate] = useState<SubState>({ kind: 'form' });
  const [form] = Form.useForm<FormValues>();

  const startMutation = useStartScrapeEstimateMutation(assistantId);
  const confirmMutation = useConfirmScrapeMutation(assistantId);

  const scoutSourceId =
    substate.kind === 'scanning' || substate.kind === 'ready' || substate.kind === 'failed'
      ? substate.sourceId
      : undefined;

  const { data: estimate, error: estimateError } = useScrapeEstimate(scoutSourceId);

  // Переключаемся в ready/failed когда поллер получит финальный статус.
  useEffect(() => {
    if (!estimate || substate.kind === 'form') return;
    if (estimate.status === 'ready' && substate.kind !== 'ready') {
      setSubstate({ kind: 'ready', sourceId: estimate.source_id });
    } else if (estimate.status === 'failed' && substate.kind !== 'failed') {
      setSubstate({ kind: 'failed', sourceId: estimate.source_id });
    }
  }, [estimate, substate]);

  // Таймаут scanning — 5 минут (см. SCOUT_TIMEOUT_MS). Если за это время
  // статус не перешёл из in_progress — что-то серьёзно сломалось,
  // показываем «failed».
  useEffect(() => {
    if (substate.kind !== 'scanning') return;
    const timer = window.setTimeout(() => {
      // Только если всё ещё scanning — переключаем в failed-симуляцию.
      // Полноценную ошибку не сохраняем, просто показываем юзеру.
      antdMessage.error('Сканирование занимает слишком много времени. Попробуйте позже.');
      setSubstate({ kind: 'form' });
    }, SCOUT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [substate.kind]);

  const handleStartScan = (values: FormValues) => {
    const url = normalizeUrl(values.url);
    if (!url) {
      antdMessage.error('Введите валидный URL начиная с https://');
      return;
    }
    startMutation.mutate(
      {
        assistant_id: assistantId,
        url,
        process_images: values.process_images,
      },
      {
        onSuccess: (data) => {
          setSubstate({ kind: 'scanning', sourceId: data.source_id });
        },
      },
    );
  };

  const handleConfirm = () => {
    if (substate.kind !== 'ready') return;
    confirmMutation.mutate(
      { source_id: substate.sourceId },
      {
        onSuccess: () => {
          antdMessage.success('Парсинг запущен — следите за прогрессом.');
          onSourceConfirmed(substate.sourceId);
        },
      },
    );
  };

  const handleBackToForm = () => {
    form.resetFields();
    setSubstate({ kind: 'form' });
  };

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={substate.kind === 'form' ? onBack : handleBackToForm}
        style={{ marginBottom: 16 }}
      >
        Назад
      </Button>

      <Title level={4} style={{ marginTop: 0 }}>
        <GlobalOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Подключить сайт
      </Title>

      {substate.kind === 'form' && (
        <FormView
          form={form}
          onSubmit={handleStartScan}
          submitting={startMutation.isPending}
          submitError={startMutation.error}
        />
      )}

      {substate.kind === 'scanning' && <ScanningView />}

      {substate.kind === 'ready' && estimate?.status === 'ready' && (
        <PreviewView
          estimate={estimate}
          onConfirm={handleConfirm}
          confirming={confirmMutation.isPending}
          confirmError={confirmMutation.error}
        />
      )}

      {substate.kind === 'failed' && estimate?.status === 'failed' && (
        <FailedView
          errorReason={estimate.error_reason}
          errorText={estimate.error}
          onRetry={handleBackToForm}
        />
      )}

      {estimateError && substate.kind !== 'form' && substate.kind !== 'failed' && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Не удалось получить статус сканирования"
          description={extractErrorMessage(estimateError)}
        />
      )}
    </div>
  );
}

// ===========================================================================
//  SUB-VIEWS
// ===========================================================================

interface FormViewProps {
  form: ReturnType<typeof Form.useForm<FormValues>>[0];
  onSubmit: (values: FormValues) => void;
  submitting: boolean;
  submitError: Error | null;
}

function FormView({ form, onSubmit, submitting, submitError }: FormViewProps) {
  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Введите адрес сайта — мы найдём sitemap.xml, посчитаем страницы и картинки,
        покажем сколько это будет стоить. До подтверждения деньги не списываются.
      </Text>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ process_images: true }}
        onFinish={onSubmit}
        style={{ maxWidth: 540 }}
      >
        <Form.Item
          label="URL сайта"
          name="url"
          rules={[
            { required: true, message: 'Введите URL' },
            {
              pattern: /^https:\/\/.+/,
              message: 'Адрес должен начинаться с https://',
            },
          ]}
        >
          <Input
            placeholder="https://example.com/"
            size="large"
            autoComplete="off"
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item name="process_images" valuePropName="checked" style={{ marginBottom: 8 }}>
          <Checkbox>
            Обрабатывать картинки на страницах
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              Ассистент сможет учитывать содержимое картинок в ответах.
              Существенно увеличивает стоимость, но даёт качественные ответы по визуальному контенту.
            </Text>
          </Checkbox>
        </Form.Item>

        {submitError && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 16, marginBottom: 16 }}
            message="Не удалось запустить сканирование"
            description={extractErrorMessage(submitError)}
          />
        )}

        <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={submitting} size="large">
            Сканировать
          </Button>
        </Form.Item>
      </Form>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 24, maxWidth: 540 }}
        message="Что важно знать"
        description={
          <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
            <li>Поддерживаются только сайты с sitemap.xml</li>
            <li>За один запуск обрабатывается до 200 страниц</li>
            <li>Сканирование (без оплаты) занимает 1–3 минуты</li>
          </ul>
        }
      />
    </>
  );
}

function ScanningView() {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <Spin
        indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
        style={{ marginBottom: 16 }}
      />
      <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
        Сканируем sitemap...
      </Title>
      <Text type="secondary">
        Это может занять 1–3 минуты. Можно закрыть страницу — сканирование продолжится в фоне.
      </Text>
    </div>
  );
}

interface PreviewViewProps {
  estimate: ScrapeEstimateReady;
  onConfirm: () => void;
  confirming: boolean;
  confirmError: Error | null;
}

function PreviewView({ estimate, onConfirm, confirming, confirmError }: PreviewViewProps) {
  const balance = parseFloat(estimate.current_balance_rub);
  const cost = parseFloat(estimate.estimated_cost_total_rub);
  const balanceAfter = balance - cost;
  // C-1/C-2: баланс после обработки может уйти в минус — это долг за
  // обработку, а не ошибка. Загрузку НЕ блокируем.
  const willBeInDebt = balanceAfter < 0;

  return (
    <>
      <Card
        size="small"
        style={{ marginBottom: 16, maxWidth: 540 }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span>Сайт проанализирован</span>
          </div>
        }
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: 'block', fontSize: 14 }}>
            {estimate.site_title ?? estimate.url}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {estimate.url}
          </Text>
        </div>

        <Row label="Страниц в sitemap:" value={String(estimate.total_pages_in_sitemap)} />
        <Row
          label="Будет обработано:"
          value={`${estimate.pages_to_process} страниц`}
          highlight={estimate.too_large}
        />
        <Row
          label="Картинок (примерно):"
          value={
            estimate.process_images
              ? `~${estimate.estimated_total_images}`
              : 'не обрабатываются'
          }
        />

        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            marginTop: 12,
            paddingTop: 12,
          }}
        >
          <Row label="Текст:" value={`${formatRub(estimate.estimated_cost_text_rub)} ₽`} />
          {estimate.process_images && (
            <Row
              label="Картинки:"
              value={`до ${formatRub(estimate.estimated_cost_images_rub)} ₽`}
            />
          )}
          <Row
            label="Итого:"
            value={`до ${formatRub(estimate.estimated_cost_total_rub)} ₽`}
            bold
          />
        </div>

        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            marginTop: 12,
            paddingTop: 12,
          }}
        >
          <Row label="Баланс аккаунта:" value={`${formatRub(estimate.current_balance_rub)} ₽`} />
          <Row
            label="После обработки:"
            value={`${balanceAfter.toFixed(2)} ₽`}
          />
        </div>
      </Card>

      {estimate.too_large && estimate.contacts && (
        <TooLargeBanner contacts={estimate.contacts} />
      )}

      {willBeInDebt && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, maxWidth: 540 }}
          message="Спишется при пополнении баланса"
          description="Сайт обработается сейчас. Стоимость спишется с баланса аккаунта — пополните его перед публикацией в каналах."
        />
      )}

      {confirmError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16, maxWidth: 540 }}
          message="Не удалось подтвердить парсинг"
          description={extractScrapeConfirmError(confirmError)}
        />
      )}

      <Button
        type="primary"
        size="large"
        onClick={onConfirm}
        loading={confirming}
      >
        Подтвердить и запустить парсинг
      </Button>
    </>
  );
}

interface RowProps {
  label: string;
  value: string;
  bold?: boolean;
  highlight?: boolean;
  error?: boolean;
}

function Row({ label, value, bold, highlight, error }: RowProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontSize: 13,
      }}
    >
      <Text type="secondary" style={{ fontSize: 13 }}>
        {label}
      </Text>
      <Text
        strong={bold}
        style={{
          fontSize: 13,
          color: error ? '#ff4d4f' : highlight ? '#fa8c16' : undefined,
        }}
      >
        {value}
      </Text>
    </div>
  );
}

interface FailedViewProps {
  errorReason: ScrapeEstimateErrorReason;
  errorText: string;
  onRetry: () => void;
}

function FailedView({ errorReason, errorText, onRetry }: FailedViewProps) {
  const message = ERROR_MESSAGES[errorReason] ?? errorText;
  const showRetry = errorReason !== 'no_sitemap';

  return (
    <>
      <Alert
        type="error"
        showIcon
        icon={<ExclamationCircleOutlined />}
        message="Не удалось обработать сайт"
        description={message}
        style={{ marginBottom: 16, maxWidth: 540 }}
      />
      {errorReason === 'no_sitemap' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, maxWidth: 540 }}
          message="Что можно сделать"
          description={
            <span>
              Загрузите документы (PDF, DOCX, PPTX, XLSX, TXT) — ассистент будет
              работать на их основе. Если нужна обработка такого сайта, свяжитесь
              с продажами в Telegram:{' '}
              <Link href="https://t.me/RiChat_sales" target="_blank">
                @RiChat_sales
              </Link>
            </span>
          }
        />
      )}
      <Button onClick={onRetry} type={showRetry ? 'primary' : 'default'}>
        {showRetry ? 'Попробовать другой сайт' : 'Назад'}
      </Button>
    </>
  );
}

function TooLargeBanner({ contacts }: { contacts: ScrapeContacts }) {
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16, maxWidth: 540 }}
      message="Сайт большой — обработаем только первые 200 страниц"
      description={
        <span>
          {contacts.message ??
            'Для полной обработки сайта свяжитесь с отделом продаж.'}
          <br />
          <Link href={contacts.telegram} target="_blank" rel="noopener">
            Связаться в Telegram
          </Link>
          {contacts.email && (
            <>
              {' · '}
              <Link href={`mailto:${contacts.email}`}>{contacts.email}</Link>
            </>
          )}
        </span>
      }
    />
  );
}

// ===========================================================================
//  HELPERS
// ===========================================================================

const ERROR_MESSAGES: Record<ScrapeEstimateErrorReason, string> = {
  no_sitemap:
    'У сайта нет sitemap.xml — мы не сможем его обработать через парсер. Загрузите документы или обратитесь в отдел продаж для индивидуальной настройки.',
  cloudflare_blocked:
    'Сайт блокирует наш парсер защитой Cloudflare. Попробуйте ещё раз через несколько минут или обратитесь в отдел продаж.',
  invalid_url:
    'Неверный URL. Проверьте что адрес начинается с https:// и сайт доступен.',
  timeout:
    'Сайт слишком долго отвечает. Попробуйте ещё раз через несколько минут.',
  internal_error:
    'Не удалось обработать сайт из-за внутренней ошибки. Попробуйте ещё раз или обратитесь в поддержку.',
};

/**
 * Нормализация URL — добавляет trailing slash если нет, тримит пробелы.
 * Возвращает null если URL пустой или не валиден.
 */
function normalizeUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Базовая проверка URL — детальная проверка на бэке
  if (!/^https:\/\/[^/]+/.test(trimmed)) return null;
  // Добавим trailing slash для корня без пути
  if (/^https:\/\/[^/]+$/.test(trimmed)) {
    return trimmed + '/';
  }
  return trimmed;
}

/** Форматирует Decimal-строку с двумя знаками после точки. */
function formatRub(value: string): string {
  const num = parseFloat(value);
  return num.toFixed(2);
}

/**
 * Особые ошибки confirm-эндпоинта:
 *  - 402: недостаточно средств
 *  - 410: scout-результат истёк (TTL 1 час)
 *  - 400: неверный статус (повторный confirm)
 */
function extractScrapeConfirmError(error: Error): string {
  const httpStatus = (error as { response?: { status?: number } }).response?.status;
  if (httpStatus === 402) {
    return 'Недостаточно средств на балансе аккаунта. Пополните баланс в разделе «Биллинг».';
  }
  if (httpStatus === 410) {
    return 'Срок действия превью истёк. Закройте эту форму и пересканируйте сайт.';
  }
  if (httpStatus === 400) {
    return 'Парсинг этого сайта уже запускался. Проверьте список источников слева.';
  }
  return extractErrorMessage(error);
}
